import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import LoginSuccessModal from './components/common/LoginSuccessModal';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastContainer } from './components/common/Toast';
import useAuthStore from './store/authStore';
import { useCartPersistence } from './hooks/useCartPersistence';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Locations = lazy(() => import('./pages/Locations'));
const Orders = lazy(() => import('./pages/Orders'));
const Profile = lazy(() => import('./pages/Profile'));

// Admin pages (these are typically less frequently visited)
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminLocations = lazy(() => import('./pages/admin/Locations'));
const AdminCustomers = lazy(() => import('./pages/admin/Customers'));
const NewProduct = lazy(() => import('./pages/admin/NewProduct'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-[calc(100vh-200px)]">
    <LoadingSpinner size="lg" />
  </div>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const ProtectedRoute: React.FC<{ 
  element: React.ReactNode;
  isAllowed: boolean;
  redirectPath?: string;
}> = ({ element, isAllowed, redirectPath = '/login' }) => {
  if (!isAllowed) {
    return <Navigate to={redirectPath} replace />;
  }
  return <>{element}</>;
};

// Component to conditionally render header based on route
const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);
  const [prevAuthState, setPrevAuthState] = useState(false);
  
  // Initialize cart persistence
  useCartPersistence();
  
  // Show login success modal when user successfully logs in
  React.useEffect(() => {
    // If user wasn't authenticated before but is now, show the success modal
    if (!prevAuthState && isAuthenticated) {
      setShowLoginSuccess(true);
    }
    
    // Update previous state
    setPrevAuthState(isAuthenticated);
  }, [isAuthenticated, prevAuthState]);
  
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <div className="flex flex-col min-h-screen">
      {/* Only show Header on non-admin pages */}
      {!isAdminRoute && <Header showAuthStatus={false} />}
      {showLoginSuccess && (
        <LoginSuccessModal onClose={() => setShowLoginSuccess(false)} />
      )}
      <main className={`flex-grow bg-gray-50 ${!isAdminRoute ? '' : 'pt-0'}`}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/locations" element={<Locations />} />
            
            <Route 
              path="/checkout" 
              element={
                <ProtectedRoute 
                  element={<Checkout />} 
                  isAllowed={isAuthenticated}
                />
              } 
            />
            
            <Route 
              path="/orders" 
              element={
                <ProtectedRoute 
                  element={<Orders />} 
                  isAllowed={isAuthenticated}
                />
              } 
            />
            
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute 
                  element={<Profile />} 
                  isAllowed={isAuthenticated}
                />
              } 
            />
            
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <Login />
              } 
            />
              <Route 
              path="/register" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <Register />
              } 
            />
            
            <Route 
              path="/forgot-password" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />
              } 
            />
            
            <Route 
              path="/reset-password" 
              element={<ResetPassword />} 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute 
                  element={<AdminLayout />}
                  isAllowed={isAuthenticated && useAuthStore.getState().isAdmin}
                  redirectPath="/"
                />
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<NewProduct />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="customers" element={<AdminCustomers />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  const { checkAuth, isLoading } = useAuthStore();
  
  // Run initial auth check
  React.useEffect(() => {
    const initialCheck = async () => {
      await checkAuth();
    };
    initialCheck();
  }, [checkAuth]);
  
  if (isLoading) {
    return <PageLoader />;
  }
    return (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <Router>
          <AppContent />
          <ToastContainer />
        </Router>
      </NotificationProvider>
    </QueryClientProvider>
  );
};

export default App;